import {
  APPOINTMENT_CHANNEL,
  APPOINTMENT_STATUS,
  Appointment,
  AptPatientProfile,
  DoctorsInBusiness,
  PLATFORM_SOURCE,
  PatientOverview,
  QUEUE_TYPE,
  USER_PERSONA,
} from "@elixir/types";
import { isSameDay, printRx } from "@elixir/utils";
import { useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import React, { PropsWithChildren, useEffect } from "react";
import { PatientAppointmentStateUi } from "ui";
import useCheckInAppointment from "../../apis/tryst/check-in-appointment";
import { LocalAppointment } from "../../apis/tryst/get-all-queues";
import useGetQueueConfig from "../../apis/tryst/get-queue-config";
import usePostBookSlot from "../../apis/tryst/post-book-slot";
import usePostEndVisit from "../../apis/tryst/post-end-visit";
import { RendererParams } from "../../react-app-env";
import useQueueStore from "../../stores/queue-store";
import { getQueueTypeFromStatus } from "../../utils/appointment-utils";
import {
  getDoc,
  getLocalVisitByIds,
  navigate,
  onAppointmentAction,
} from "../../utils/bridges/methods";
import { track } from "../../utils/tracking";
import { useMyAccessProfileV2 } from "../../apis/tryst/get-my-access-profile";
import {
  convertLocalPatientToAuthPatient,
  firebaseAptToAppointment,
} from "../../controllers/appointments/utils/converter-utilts";
import getB from "../../utils/bridges/EkaBridge";
import getPatientOverview from "../../apis/common/get-patient-overview";
import { EKA_EVENT } from "../../utils/bridges";

const RenderAppointmentWidget = (
  props: PropsWithChildren<Partial<RendererParams>>
) => {
  const [isPrintRxLoading, setIsPrintRxLoading] =
    React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  useEffect(() => {
    dispatchEvent(
      new CustomEvent(EKA_EVENT, {
        detail: { type: "LOADING_CAHNGED", value: isLoading },
      })
    );
  }, [isLoading]);

  const mode = useQueueStore((state) => state.mode);
  const clinicId = useQueueStore((state) => state.clinicId);

  const actualClinicId = React.useMemo(() => {
    const pathname = window.location.pathname;

    // Define a regular expression pattern to match the clinic ID
    // eslint-disable-next-line no-useless-escape
    const regex = /\/clinic\/([^\/]+)/;

    // Use the match method to extract the clinic ID
    const match = pathname.match(regex);

    const urlClinicId = match ? match[1] : "";

    const lsClinicId = localStorage.getItem("selectedClinicId") as string;

    return clinicId || urlClinicId || lsClinicId;
  }, [clinicId]);

  const setSelectedDate = useQueueStore((state) => state.setSelectedDate);
  const setLeftPanelQueueType = useQueueStore(
    (state) => state.setLeftPanelQueueType
  );

  const setOpenLeftPanel = useQueueStore((state) => state.setOpenLeftPanel);
  const setSelectedPatient = useQueueStore((state) => state.setSelectedPatient);
  const setShowSelectSlot = useQueueStore((state) => state.setShowSelectSlot);
  const setScrollToValues = useQueueStore((state) => state.setScrollToValues);

  const { data: queueConfigData } = useGetQueueConfig();
  const [docs, setDocs] = React.useState<DoctorsInBusiness[]>([]);
  const [patientOverviewData, setPatientOverviewData] = React.useState<
    PatientOverview | undefined
  >(undefined);

  const patient_oid = props.ptOid;

  const { mutateAsync: putCheckInAppointment, isLoading: isCheckinLoading } =
    useCheckInAppointment();
  const { mutateAsync: postBookSlot, isLoading: isPostBookSlotLoading } =
    usePostBookSlot();
  const { mutateAsync: putEndVisit, isLoading: isVisitEndLoading } =
    usePostEndVisit();

  useEffect(() => {
    const fetchPatientOverview = async () => {
      setIsLoading(true);
      const data = await getPatientOverview({ patientId: patient_oid });
      setPatientOverviewData(data?.patientOverview);
      dispatchEvent(
        new CustomEvent(EKA_EVENT, {
          detail: {
            type: "SEND_PATIENT_OVERVIEW",
            value: {
              patient: data?.patient,
              last_visit: data?.patientOverview?.last_visit,
            },
          },
        })
      );
      setIsLoading(false);
    };
    fetchPatientOverview();
  }, [patient_oid]);

  React.useEffect(() => {
    const setDocFromBridge = async () => {
      const data = await getDoc();
      if (data) {
        const { _id, profile } = data;
        const { personal, professional } = profile;
        const { name, pic } = personal;
        const { username } = professional;

        setDocs([
          {
            username: username,
            discovery_id: 0,
            oid: _id,
            name: `${name.f} ${name.l}`,
            pic,
          },
        ]);
      }
    };

    track("gs_apt_card", {
      persona: isStaff ? "staff" : "dr",
    });

    setDocFromBridge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: myAccessProfile } = useMyAccessProfileV2();

  const [isDoctor, setIsDoctor] = React.useState<boolean>(true);
  const [isStaff, setIsStaff] = React.useState<boolean>(false);

  const [isDocStartVisit, setIsDocStartVisit] = React.useState<boolean>(false);

  React.useEffect(() => {
    const setPersona = async () => {
      const resp = await getDoc();
      const role = resp?.loginmeta?.role;
      setIsStaff(role === "STAFF");
      setIsDoctor(role === "DOCTOR");
    };
    setPersona();
  }, []);

  const handleStartVisit = async () => {
    const newDateObject = new Date();
    setIsDocStartVisit(true);

    const { success, data } = await postBookSlot({
      doctorId: myAccessProfile?.loginmeta?.docid!,
      clinicId: actualClinicId,
      patient: {
        oid: patient_oid,
        allowMultiBooking:
          queueConfigData?.data?.apt_config?.allow_pt_multi_apt,
      },
      tags: [],
      startTime: newDateObject.toISOString(),
      endTime: newDateObject.toISOString(),
      action: APPOINTMENT_STATUS.CHECKED_IN,
      channel: APPOINTMENT_CHANNEL.WALKIN,
      mode,
    });
    setIsDocStartVisit(false);
    if (success && data) {
      const patientDataRequest = getB().getPatientsByIds([patient_oid]);
      const localVisitRequest = getLocalVisitByIds([data.aid]);

      const [[patientData] = [], localVisits] = await Promise.all([
        patientDataRequest,
        localVisitRequest,
      ]);

      const appointment = firebaseAptToAppointment(
        data,
        convertLocalPatientToAuthPatient(patientData),
        patientData?.formData || []
      );

      const updatedAptObject: LocalAppointment = {
        ...appointment,
        isLocalPresnt: localVisits[0] !== undefined,
        serialNum: 1,
        localRxId: localVisits[0]?.prescriptionId,
        patient: patientOverviewData?.patient as AptPatientProfile,
      };

      onAppointmentAction({
        a: updatedAptObject,
        cId: data.clinic_id,
      });
    } else {
      notification.error({
        message: "Unable to start visit for the patient, please try again",
        placement: "bottomRight",
      });
    }
  };

  const handleCheckin = async () => {
    const newDateObject = new Date();
    const { success } = await postBookSlot({
      channel: APPOINTMENT_CHANNEL.WALKIN,
      clinicId: actualClinicId,
      doctorId: myAccessProfile?.loginmeta?.docid!,
      mode,
      startTime: newDateObject.toISOString(),
      endTime: newDateObject.toISOString(),
      patient: { oid: patient_oid },
      action: APPOINTMENT_STATUS.CHECKED_IN,
      tags: [],
    });
    if (success) {
      navigate(
        "push",
        `/app/appointments/clinic/${clinicId}/queue?right=checkin`
      );
    } else {
      notification.error({
        message: "Unable to checkin the patient, please try again",
        placement: "bottomRight",
      });
    }
  };

  const handleBook = async () => {
    setLeftPanelQueueType("add-new");
    setShowSelectSlot(true);
    setOpenLeftPanel(true);
    setSelectedPatient(patientOverviewData?.patient);
    navigate("push", `/app/appointments/clinic/${clinicId}/queue?left=add-new`);
  };

  const handleCheckinBookedAppointment = async (apt: Appointment) => {
    const { success } = await putCheckInAppointment({ appointment: apt });
    if (success) {
      handleGoToAptClick(apt);
      notification.success({
        message: "Appointment checked in successfully!",
        placement: "bottomRight",
      });
      setSelectedDate(new Date());
      if (document.body.clientWidth < 1280) {
        setOpenLeftPanel(false);
      }
      setScrollToValues({ aid: apt.aid, queueType: QUEUE_TYPE.CHECKED_IN });
    } else {
      notification.error({
        message: "Failed to checkin appointment, please try again.",
        placement: "bottomRight",
      });
    }
  };

  const handleEmptySecondaryClick = () => {
    if (isDoctor) {
      track("apt_card_doc_no_apt_checkin_click");
      handleCheckin();
    } else {
      track("apt_card_staff_no_apt_book_click");
      handleBook();
    }
  };

  const handleEmptyPrimaryClick = () => {
    if (isDoctor) {
      track("apt_card_doc_no_apt_start_visit_click");
      handleStartVisit();
    } else {
      track("apt_card_staff_no_apt_checkin_click");
      handleCheckin();
    }
  };

  const handlePrimaryClick = async (apt: Appointment) => {
    track("apt_card_staff_apt_primary_click", {
      persona: isStaff ? "staff" : "dr",
      status: apt.status,
    });
    if (isStaff) {
      if (
        [
          APPOINTMENT_STATUS.BOOKED,
          APPOINTMENT_STATUS.RESCHEDULED,
          APPOINTMENT_STATUS.INITIATED,
          APPOINTMENT_STATUS.RESERVED,
          APPOINTMENT_STATUS.PROVISIONAL_CANCELLED,
        ].includes(apt.status)
      ) {
        handleCheckinBookedAppointment(apt);
        return;
      }

      if (
        [APPOINTMENT_STATUS.CHECKED_IN, APPOINTMENT_STATUS.ONGOING].includes(
          apt.status
        )
      ) {
        const { aid, clinic_id, doctor_oid, patient_oid, start_time, patient } =
          apt;
        const { success } = await putEndVisit({
          status: APPOINTMENT_STATUS.COMPLETED_NO_PRESCRIPTION,
          aid: aid,
          doctor: { oid: doctor_oid, clinic: clinic_id },
          pid: {
            oid: patient_oid,
          },
          visit: {
            st: start_time,
            et: new Date().toISOString(),
          },
        });

        if (success) {
          handleGoToAptClick(apt);
          notification.success({
            message: `Checked out ${patient.fln} successfully`,
            placement: "bottomRight",
          });
        } else {
          notification.error({
            message: `Failed to Check Out ${patient.fln}`,
            placement: "bottomRight",
          });
        }
        return;
      }

      if (apt.status === APPOINTMENT_STATUS.COMPLETED) {
        const { prescription_url } = apt;
        setIsPrintRxLoading(true);
        await printRx(prescription_url);
        setIsPrintRxLoading(false);
        return;
      }
      return;
    }
    if (
      [
        APPOINTMENT_STATUS.COMPLETED,
        APPOINTMENT_STATUS.COMPLETED_NO_PRESCRIPTION,
      ].includes(apt.status)
    ) {
      handleStartVisit();
      return;
    }
    const localVisits = await getLocalVisitByIds([apt.aid]);
    const updatedAptObject = {
      ...apt,
      isLocalPresnt: localVisits[0] !== undefined,
      serialNum: 1,
      localRxId: localVisits[0]?.prescriptionId,
    };
    onAppointmentAction({
      a: updatedAptObject,
      cId: apt.clinic_id,
    });
  };

  const handleSecondaryClick = async (apt: Appointment) => {
    track("apt_card_staff_apt_secondary_click", {
      persona: isStaff ? "staff" : "dr",
      status: apt.status,
    });
    if (isStaff) {
      handleGoToAptClick(apt);
      return;
    }

    if (
      [APPOINTMENT_STATUS.BOOKED, APPOINTMENT_STATUS.PARKED].includes(
        apt.status
      )
    ) {
      if (!isSameDay(new Date(apt.date), new Date())) {
        handleGoToAptClick(apt);
        return;
      }
      handleCheckinBookedAppointment(apt);
      return;
    }

    if (apt.status === APPOINTMENT_STATUS.CHECKED_IN) {
      navigate("push", `/app/patient/${apt.patient_oid}/patient-overview`);
      return;
    }

    if (apt.status === APPOINTMENT_STATUS.ONGOING) {
      const { aid, clinic_id, doctor_oid, patient_oid, start_time, patient } =
        apt;
      const { success } = await putEndVisit({
        status: APPOINTMENT_STATUS.COMPLETED_NO_PRESCRIPTION,
        aid: aid,
        doctor: { oid: doctor_oid, clinic: clinic_id },
        pid: { oid: patient_oid },
        visit: { st: start_time, et: new Date().toISOString() },
      });

      if (success) {
        handleGoToAptClick(apt);
        notification.success({
          message: `Checked out ${patient.fln} successfully`,
          placement: "bottomRight",
        });
      } else {
        notification.error({
          message: `Failed to Check Out ${patient.fln}`,
          placement: "bottomRight",
        });
      }
      return;
    }

    if (
      [
        APPOINTMENT_STATUS.COMPLETED,
        APPOINTMENT_STATUS.COMPLETED_NO_PRESCRIPTION,
      ].includes(apt.status)
    ) {
      handleBook();
      return;
    }

    if (
      [
        APPOINTMENT_STATUS.INITIATED,
        APPOINTMENT_STATUS.RESERVED,
        APPOINTMENT_STATUS.PROVISIONAL_CANCELLED,
        APPOINTMENT_STATUS.RESCHEDULED,
      ].includes(apt.status)
    ) {
      if (!isSameDay(new Date(apt.date), new Date())) {
        handleGoToAptClick(apt);
        return;
      }
      handleCheckinBookedAppointment(apt);
      return;
    }
  };

  const handleGoToAptClick = (apt: Appointment) => {
    const { date } = apt;
    setSelectedDate(new Date(date));
    const queueType = getQueueTypeFromStatus(apt.status);
    let queueTypeParams = "";
    if (queueType === "checkin" || queueType === "completed") {
      queueTypeParams = `right=${queueType}`;
    } else {
      queueTypeParams = `left=${queueType}`;
    }

    setScrollToValues({ aid: apt.aid, queueType });

    navigate(
      "push",
      `/app/appointments/clinic/${clinicId}/queue${
        queueType ? `?${queueTypeParams}` : ""
      }`
    );
  };

  const isPrimaryButtonLoading = isStaff
    ? isCheckinLoading || isVisitEndLoading || isPrintRxLoading
    : isDocStartVisit && isPostBookSlotLoading;

  const isSecondaryButtonLoading = isStaff
    ? isPostBookSlotLoading
    : (!isDocStartVisit && isPostBookSlotLoading) ||
      isCheckinLoading ||
      isVisitEndLoading;

  return (
    <PatientAppointmentStateUi
      patientOverviewData={patientOverviewData}
      doctors={docs}
      tags={queueConfigData?.data?.tags || []}
      userPersona={isStaff ? USER_PERSONA.STAFF : USER_PERSONA.DOC}
      platform={PLATFORM_SOURCE.DR}
      emptyPrimaryClick={handleEmptyPrimaryClick}
      emptySecondaryClick={handleEmptySecondaryClick}
      primaryActionClick={handlePrimaryClick}
      secondaryActionClick={handleSecondaryClick}
      isPrimaryLoading={isPrimaryButtonLoading}
      isSecondaryLoading={isSecondaryButtonLoading}
      isLoading={isLoading}
    />
  );
};

export default RenderAppointmentWidget;
